import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { jsPDF } from 'jspdf';
import { Download, X as XIcon, Share2, MessageCircle, Loader2 } from 'lucide-react';
import { Aurora, CycleWord, Reveal, Nav, Wordmark, EventVisual, Rule, AdSense } from './components';
import { About } from './pages/About';
import { Privacy } from './pages/Privacy';
import { Terms } from './pages/Terms';
import { Cookies } from './pages/Cookies';
import { Blog } from './pages/Blog';
import { ForOrganizers } from './pages/ForOrganizers';
import { Consultation } from './pages/Consultation';
import { Playbook } from './pages/Playbook';
import { Photography } from './pages/Photography';
import { Shop } from './pages/Shop';

// Cookie Banner Component
const CookieBanner = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) setShow(true);
  }, []);
  const accept = () => {
    localStorage.setItem('cookieConsent', 'all');
    setShow(false);
  };
  const reject = () => {
    localStorage.setItem('cookieConsent', 'essential');
    setShow(false);
  };
  if (!show) return null;
  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0,
      background: 'var(--bg)', borderTop: '1px solid var(--line)',
      padding: '20px 24px', zIndex: 9999,
      boxShadow: '0 -4px 20px rgba(0,0,0,0.1)',
    }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ flex: 1, minWidth: 280 }}>
          <p style={{ fontSize: 14, color: 'var(--ink-2)', margin: 0, lineHeight: 1.6 }}>
            We use cookies to improve your experience and deliver personalized content. 
            <Link to="/cookies" style={{ color: 'var(--primary)', textDecoration: 'underline' }}>Learn more</Link>
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button onClick={reject} style={{
            padding: '10px 18px', fontSize: 13, fontWeight: 500,
            border: '1px solid var(--line)', borderRadius: 8,
            background: 'transparent', color: 'var(--ink-2)', cursor: 'pointer'
          }}>
            Essential Only
          </button>
          <button onClick={accept} style={{
            padding: '10px 18px', fontSize: 13, fontWeight: 600,
            border: 'none', borderRadius: 8,
            background: 'var(--primary)', color: '#fff', cursor: 'pointer'
          }}>
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
};

// Mobile detection hook
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  return isMobile;
};

// Trust metrics used across sections
const trustItems = [
  'THOUSANDS of guides generated',
  'Available for all event types!',
  'Trusted by solo attendees & groups',
];

// Tweak panel component
const TweaksPanel = ({ tweaks, setTweaks, open, onClose }) => {
  const update = (k, v) => {
    const next = { ...tweaks, [k]: v };
    setTweaks(next);
    window.__TWEAKS__ = next;
    window.parent?.postMessage({ type: '__edit_mode_set_keys', edits: { [k]: v } }, '*');
  };
  return (
    <div className={'tweaks-panel' + (open ? ' open' : '')}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontFamily: 'var(--font-serif)', fontSize: 18 }}>Tweaks</div>
        <button onClick={onClose} style={{ color: 'var(--ink-3)', fontSize: 18, padding: 4 }}>×</button>
      </div>

      <div className="tweaks-row">
        <label>Title font</label>
        <div className="tweaks-options">
          {[['serif','Instrument Serif'],['sans','Plus Jakarta']].map(([v,l]) => (
            <button key={v} className={tweaks.titleFont === v ? 'is-on' : ''} onClick={() => update('titleFont', v)}>{l}</button>
          ))}
        </div>
      </div>

      <div className="tweaks-row">
        <label>Primary</label>
        <div className="tweaks-options">
          {['aqua','teal'].map(v => (
            <button key={v} className={tweaks.primary === v ? 'is-on' : ''} onClick={() => update('primary', v)}>{v}</button>
          ))}
        </div>
      </div>

      <div className="tweaks-row">
        <label>Aurora intensity</label>
        <div className="tweaks-options">
          {['subtle','medium','vivid'].map(v => (
            <button key={v} className={tweaks.auroraIntensity === v ? 'is-on' : ''} onClick={() => update('auroraIntensity', v)}>{v}</button>
          ))}
        </div>
      </div>

      <div className="tweaks-row">
        <label>Film grain</label>
        <div className="tweaks-options">
          {[true, false].map(v => (
            <button key={String(v)} className={tweaks.grainOn === v ? 'is-on' : ''} onClick={() => update('grainOn', v)}>{v ? 'on' : 'off'}</button>
          ))}
        </div>
      </div>

      <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 8, lineHeight: 1.5 }}>
        Changes persist to the file. Toggle off with the toolbar button.
      </div>
    </div>
  );
};

// Hero Section
const Hero = () => {
  const isMobile = useIsMobile();
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const { t, i18n } = useTranslation();
  const [guide, setGuide] = useState(null);
  const [error, setError] = useState(null);
  const guideRef = useRef(null);
  const downloadBannerRef = useRef(null);

  // Check for prefill URL from blog post CTA
  useEffect(() => {
    const prefillUrl = sessionStorage.getItem('prefillUrl');
    if (prefillUrl) {
      setUrl(prefillUrl);
      sessionStorage.removeItem('prefillUrl');
      // Auto-generate guide after a short delay to allow render
      setTimeout(() => {
        if (isValidUrl(prefillUrl)) {
          generateFromUrl(prefillUrl);
        }
      }, 500);
    }
  }, []);

  const generateFromUrl = async (targetUrl) => {
    setError(null);
    setLoading(true);
    setGuide(null);
    try {
      const response = await fetch('/api/gemini-intel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: targetUrl, website: '' })
      });
      
      if (!response.ok) {
        if (response.status === 429) {
          const data = await response.json();
          setError(data.error || 'Too many requests. Please wait a moment and try again.');
          return;
        }
        if (import.meta.env.DEV) {
          await new Promise(r => setTimeout(r, 1200));
          setGuide(MOCK_GUIDE);
          setTimeout(() => guideRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
          return;
        }
        throw new Error(`API error: ${response.status}`);
      }
      
      const data = await response.json();
      setGuide(data);
      setTimeout(() => guideRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } catch (e) {
      if (import.meta.env.DEV) {
        await new Promise(r => setTimeout(r, 1200));
        setGuide(MOCK_GUIDE);
        return;
      }
      console.error('Event Intel error:', e);
      setError('Something went wrong generating your guide. Try again or paste a different URL.');
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = () => {
    if (!guide) return;

    const doc = new jsPDF({ unit: 'mm', format: 'letter', orientation: 'portrait' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const contentWidth = pageWidth - margin * 2;
    let y = 25;

    const checkPage = (needed = 12) => {
      if (y + needed > 270) { doc.addPage(); y = 25; }
    };

    const addLabel = (label) => {
      checkPage(10);
      doc.setFontSize(9);
      doc.setTextColor(102, 102, 128);
      doc.text(label.toUpperCase(), margin, y);
      y += 6;
    };

    const addText = (text) => {
      checkPage(8);
      doc.setFontSize(11);
      doc.setTextColor(26, 26, 46);
      const lines = doc.splitTextToSize(text, contentWidth);
      checkPage(lines.length * 5.5);
      doc.text(lines, margin, y);
      y += lines.length * 5.5 + 6;
    };

    const addBullets = (items) => {
      doc.setFontSize(11);
      doc.setTextColor(26, 26, 46);
      items.forEach(item => {
        const lines = doc.splitTextToSize(`• ${item}`, contentWidth - 4);
        checkPage(lines.length * 5.5);
        doc.text(lines, margin + 2, y);
        y += lines.length * 5.5 + 3;
      });
      y += 3;
    };

    // Header
    doc.setFontSize(9);
    doc.setTextColor(128, 128, 128);
    doc.text(`VentyVIP Intel Guide  ·  ${guide.eventType || 'Event'}`, margin, y);
    y += 10;

    doc.setFontSize(22);
    doc.setTextColor(26, 26, 46);
    const nameLines = doc.splitTextToSize(guide.eventName || 'Event Guide', contentWidth);
    doc.text(nameLines, margin, y);
    y += nameLines.length * 10 + 4;

    if (guide.oneLiner) {
      doc.setFontSize(12);
      doc.setTextColor(74, 74, 94);
      const lines = doc.splitTextToSize(guide.oneLiner, contentWidth);
      doc.text(lines, margin, y);
      y += lines.length * 6 + 10;
    }

    // Divider
    doc.setDrawColor(200, 200, 210);
    doc.line(margin, y, pageWidth - margin, y);
    y += 10;

    // Fields
    const fields = [
      { label: 'Date & Time', value: guide.date },
      { label: 'Location', value: guide.location },
      { label: 'Weather', value: guide.weather },
      { label: 'What to Bring', value: guide.whatToBring, isList: true },
      { label: 'Parking', value: guide.parkingInfo },
      { label: 'Public Transit', value: guide.publicTransit },
      { label: 'Dress Code', value: guide.dressCode },
      { label: 'When to Arrive', value: guide.arrivalTime },
      { label: 'The Vibe', value: guide.vibe },
    ];

    fields.forEach(({ label, value, isList }) => {
      if (!value || (Array.isArray(value) && value.length === 0)) return;
      addLabel(label);
      if (isList && Array.isArray(value)) addBullets(value);
      else addText(String(value));
    });

    // Contact
    if (guide.contact && typeof guide.contact === 'object') {
      addLabel('Contact');
      const c = guide.contact;
      const parts = [
        c.organizer,
        c.website,
        c.email,
        c.instagram ? `Instagram: ${c.instagram}` : null,
        c.twitter ? `X/Twitter: ${c.twitter}` : null,
        c.facebook ? `Facebook: ${c.facebook}` : null,
      ].filter(Boolean);
      addBullets(parts);
    }

    // Footer
    checkPage(16);
    doc.setDrawColor(200, 200, 210);
    doc.line(margin, y, pageWidth - margin, y);
    y += 6;
    doc.setFontSize(8);
    doc.setTextColor(153, 154, 170);
    const generated = `Guide generated on ${new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`;
    doc.text(generated, margin, y);
    y += 5;
    const disclaimer = doc.splitTextToSize(
      'Be sure to do a final check of your event\'s website for any recent updates. VentyVIP is not responsible for misinformation or loss of event value.',
      contentWidth
    );
    doc.text(disclaimer, margin, y);
    y += disclaimer.length * 4 + 4;
    doc.setFontSize(9);
    doc.setTextColor(128, 128, 128);
    doc.text('Generated by VentyVIP — ventyvip.com', margin, y);

    const safeName = (guide.eventName || 'event').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    doc.save(`ventyvip-${safeName}.pdf`);
  };

  const isValidUrl = (s) => {
    try { new URL(s.startsWith('http') ? s : 'https://' + s); return s.includes('.'); }
    catch { return false; }
  };

  const MOCK_GUIDE = {
    eventName: 'Brooklyn Half Marathon',
    eventType: 'Running Race',
    oneLiner: 'A scenic 13.1-mile race through Prospect Park and the Brooklyn waterfront.',
    date: 'Saturday, May 16 · 7:00 AM',
    location: 'Prospect Park, Brooklyn, NY',
    contact: { organizer: 'NYRR', website: 'https://nyrr.org/races', email: 'help@nyrr.org', instagram: '@nyrr', twitter: '@nyrr', facebook: 'nyrr' },
    weather: 'Cool and partly cloudy, 48–58°F. Light wind. Good running conditions.',
    whatToBring: ['Photo ID', 'Race bib (download from NYRR app)', 'Comfortable running shoes', 'Water bottle', 'Light layers for warmup'],
    parkingInfo: 'Limited street parking near Prospect Park. Arrive 45+ min early for best spots.',
    publicTransit: 'B/Q trains to Prospect Park station (2 blocks to start). 2/3 trains to Grand Army Plaza (10-minute walk). Several bus routes serve the area.',
    dressCode: 'Athletic wear appropriate for the weather. Layers recommended for early morning start.',
    arrivalTime: 'Doors open at 6:00 AM. Race starts at 7:00 AM. Arrive by 6:30 AM for bag check.',
    vibe: 'High energy and motivating. Thousands of runners cheering each other on. Great for first-timers and soloists.',
    confidence: 0.9,
  };

  const generate = async () => {
    if (!url.trim() || !isValidUrl(url)) {
      setError('Please paste a valid event URL to continue.');
      return;
    }
    await generateFromUrl(url);
  };

  return (
    <section style={{ position: 'relative', paddingTop: 72, paddingBottom: 96, overflow: 'hidden' }}>
      <Aurora intensity={window.__TWEAKS__.auroraIntensity} grain={window.__TWEAKS__.grainOn}/>
      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        <Reveal delay={0} style={{ display: 'flex', justifyContent: 'center', marginBottom: 32 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 10,
            padding: '6px 16px 6px 6px',
            borderRadius: 999,
            background: 'oklch(1 0 0 / 0.65)',
            backdropFilter: 'blur(8px)',
            border: '1px solid var(--line)',
            fontSize: 13, color: 'var(--ink-2)',
          }}> 
            <span style={{
              padding: '3px 9px', borderRadius: 999,
              background: 'var(--primary)', color: 'white',
              fontSize: 11, fontFamily: 'var(--font-mono)', letterSpacing: '.08em',
            }}>NEW</span>
            <a href="#newsletter" style={{ color: 'var(--ink-2)' }}>Get handpicked events and discounts, in your inbox! →</a>
          </div>
        </Reveal>

        <Reveal delay={120} as="h1" style={{
          textAlign: 'center',
          fontSize: 'clamp(52px, 7.5vw, 100px)',
          lineHeight: 0.96,
          letterSpacing: '-0.03em',
          fontWeight: 600,
          maxWidth: 1060, margin: '0 auto',
        }}>
          Paste any event URL.<br/>
          Show up <CycleWord words={['confident','prepared','ready','informed']} />.
        </Reveal>

        <Reveal delay={260} as="p" style={{
          textAlign: 'center',
          maxWidth: 580,
          margin: '28px auto 0',
          fontSize: 18,
          color: 'var(--ink-2)',
          lineHeight: 1.6,
        }}>
          VentyVIP is a free event guide app for <i>any</i> event type.
          <br />
          Paste any event URL — we pull the details that actually matters so that you're more than ready for your event!
        </Reveal>

        <Reveal delay={400} style={{ marginTop: 48 }}>
          <div style={{
            display: 'flex', justifyContent: 'center', gap: 12,
            maxWidth: 720, margin: '0 auto',
          }}>
            {/* Honeypot field — hidden from users, catches bots */}
            <input type="text" name="website" style={{ display: 'none' }} tabIndex={-1} autoComplete="off" />
            <input
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && generate()}
              placeholder="Paste event URL (Eventbrite, Luma, venue site...)"
              style={{
                flex: 1,
                padding: '16px 20px',
                borderRadius: 999,
                border: `2px solid ${error ? '#e74c3c' : 'var(--line)'}`,
                background: 'var(--bg)',
                fontSize: 16,
                fontFamily: 'var(--font-sans)',
                outline: 'none',
                boxShadow: 'var(--shadow-sm)',
              }}
            />
            <button 
              onClick={generate}
              className="btn btn-primary"
              style={{ padding: '16px 28px', fontSize: 16, fontWeight: 600 }}
            >
              {loading ? 'Analyzing...' : '📄 Get Guide'}
            </button>
          </div>
          {loading && (
            <div style={{ textAlign: 'center', marginTop: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
                <Loader2 size={24} className="spin" style={{ animation: 'spin 1s linear infinite', color: 'var(--primary)' }} />
                <span style={{ fontSize: 15, color: 'var(--ink-2)', fontWeight: 500 }}>Analyzing event details...</span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 8, fontFamily: 'var(--font-mono)' }}>
                This takes about 5-10 seconds
              </div>
            </div>
          )}
          {error && (
            <div style={{ textAlign: 'center', color: '#e74c3c', fontSize: 14, marginTop: 12 }}>
              {error}
            </div>
          )}
          {guide && !error && (
            <div style={{
              textAlign: 'center', marginTop: 14,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              fontSize: 14, color: '#2a7a4b', fontWeight: 500,
            }}>
              <span style={{
                width: 18, height: 18, borderRadius: 999,
                background: '#2a7a4b', color: '#fff',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, flexShrink: 0,
              }}>✓</span>
              Your guide is ready — scroll down to view it.
            </div>
          )}
        </Reveal>

        <Reveal delay={500}>
          <div style={{
            display: 'flex', gap: 48, justifyContent: 'center',
            fontSize: 13, color: 'var(--ink-3)',
            flexWrap: 'wrap',
            marginTop: 32,
            paddingTop: 24,
            borderTop: '1px solid var(--line)',
          }}>
            {trustItems.map(l => (
              <div key={l} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <span style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--primary)' }} />
                <span>{l}</span>
              </div>
            ))}
          </div>
        </Reveal>

        {guide && (
          <div ref={guideRef} style={{ marginTop: 52, backgroundColor: '#ffffff', color: '#1a1a2e', padding: '24px' }}>
            <div style={{ backgroundColor: '#ffffff', padding: '20px' }}>
              <Reveal delay={0}>
                <div style={{
                  borderTop: '2px solid #1a1a2e',
                  paddingTop: 24,
                  marginBottom: 28,
                }}>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.14em', textTransform: 'uppercase', color: '#666680', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                    VentyVIP Intel Guide · {guide.eventType}
                    <button
                      onClick={() => downloadBannerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                      title="Download PDF"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#666680', lineHeight: 1, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Download size={14} />
                      <span style={{ fontSize: 11, letterSpacing: '.06em', fontWeight: 700, color: 'var(--primary-strong)', background: '#fef9c3', padding: '2px 6px', borderRadius: 4 }}> FREE DOWNLOAD</span>
                    </button>
                  </div>
                  <h3 style={{ fontSize: 'clamp(28px, 3.5vw, 44px)', letterSpacing: '-0.02em', color: '#1a1a2e' }}>
                    {guide.eventName}
                  </h3>
                  <p style={{ fontSize: 16, color: '#4a4a5e', marginTop: 10, maxWidth: 600, lineHeight: 1.5 }}>
                    {guide.oneLiner}
                  </p>
                </div>
              </Reveal>

              <Reveal delay={60}>
                <div style={{ display: 'grid', gap: 0, borderTop: '1px solid #e9ecef' }}>
                  {[
                    { icon: '📅', label: 'Date & Time', value: guide.date },
                    { icon: '📍', label: 'Location', value: guide.location },
                    { icon: '🌤️', label: 'Weather', value: guide.weather },
                    { icon: '🎒', label: 'What to Bring', value: guide.whatToBring, isList: true },
                    { icon: '🚗', label: 'Parking', value: guide.parkingInfo },
                    { icon: '🚇', label: 'Public Transit', value: guide.publicTransit },
                    { icon: '🎽', label: 'Dress Code', value: guide.dressCode },
                    { icon: '⏰', label: 'When to Arrive', value: guide.arrivalTime },
                    { icon: '😎', label: 'The Vibe', value: guide.vibe },
                    { icon: '📞', label: 'Contact', value: guide.contact, isContact: true },
                  ].filter(row => row.value && (Array.isArray(row.value) ? row.value.length > 0 : true)).map((row, i) => (
                    <div key={i} style={{
                      display: 'grid',
                      gridTemplateColumns: isMobile ? '1fr' : '180px 1fr',
                      gap: isMobile ? '8px' : '16px',
                      padding: isMobile ? '12px 0' : '16px 0',
                      borderBottom: '1px solid #e9ecef',
                      alignItems: 'flex-start',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 16 }}>{row.icon}</span>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.08em', textTransform: 'uppercase', color: '#666680' }}>
                          {row.label}
                        </span>
                      </div>
                      <div>
                        {row.isContact && typeof row.value === 'object' ? (
                          <div style={{ display: 'grid', gap: 6 }}>
                            {row.value.organizer && <span style={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e' }}>{row.value.organizer}</span>}
                            {row.value.website && <a href={row.value.website} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: 'var(--primary)', textDecoration: 'none' }}>🌐 {row.value.website}</a>}
                            {row.value.email && <a href={`mailto:${row.value.email}`} style={{ fontSize: 13, color: 'var(--primary)', textDecoration: 'none' }}>✉️ {row.value.email}</a>}
                            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 2 }}>
                              {row.value.instagram && <a href={`https://instagram.com/${row.value.instagram.replace('@','')}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: '#E1306C', textDecoration: 'none' }}>📸 {row.value.instagram}</a>}
                              {row.value.twitter && <a href={`https://x.com/${row.value.twitter.replace('@','')}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: '#1DA1F2', textDecoration: 'none' }}>𝕏 {row.value.twitter}</a>}
                              {row.value.facebook && <a href={`https://facebook.com/${row.value.facebook}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: '#1877F2', textDecoration: 'none' }}>📘 {row.value.facebook}</a>}
                            </div>
                          </div>
                        ) : row.isList && Array.isArray(row.value) ? (
                          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 6 }}>
                            {row.value.map((item, j) => (
                              <li key={j} style={{ fontSize: 14, color: '#1a1a2e', lineHeight: 1.5, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                                <span style={{ color: 'var(--primary)', marginTop: 2 }}>→</span>
                                {item}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <span style={{ fontSize: 14, color: '#1a1a2e', lineHeight: 1.6 }}>{row.value}</span>
                        )}
                      </div>
                    </div>
                  ))}
                  <div style={{
                    marginTop: 24,
                    paddingTop: 20,
                    borderTop: '1px solid #e9ecef',
                    display: 'flex', flexDirection: 'column', gap: 6,
                  }}>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: '#999aaa', letterSpacing: '.06em' }}>
                      Guide generated on {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </div>
                    <p style={{ fontSize: 12, color: '#999aaa', lineHeight: 1.6, maxWidth: 600, margin: 0 }}>
                      Be sure to do a final check of your event's website for any recent updates. VentyVIP is not responsible for misinformation or loss of event value.
                    </p>
                  </div>
                  <div style={{ paddingTop: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                    <button
                      onClick={() => { setGuide(null); setUrl(''); setError(null); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      className="btn btn-ghost"
                      style={{ fontSize: 13, color: '#4a4a5e', borderColor: '#d1d1db', width: 'auto' }}>
                      Try another URL
                    </button>
                    {(() => {
                      const shareText = encodeURIComponent(`I'm going to ${guide.eventName}! Get the details on VentyVIP.com!`);
                      const shareUrl = encodeURIComponent('https://ventyvip.com');
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 11, color: '#999aaa', fontFamily: 'var(--font-mono)', letterSpacing: '.06em', textTransform: 'uppercase', marginRight: 4 }}>Share</span>
                          {typeof navigator !== 'undefined' && navigator.share && (
                            <button
                              onClick={() => {
                                navigator.share({
                                  title: `I'm going to ${guide.eventName}!`,
                                  text: `Get the details on VentyVIP.com!`,
                                  url: 'https://ventyvip.com'
                                }).catch(() => {});
                              }}
                              title="Share to your apps"
                              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 999, background: 'var(--primary)', color: '#fff', fontSize: 14, border: 'none', cursor: 'pointer' }}>
                              <Share2 size={15} />
                            </button>
                          )}
                          <a
                            href={`https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`}
                            target="_blank" rel="noopener noreferrer"
                            title="Share on X"
                            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 999, background: '#000', color: '#fff', fontSize: 14, textDecoration: 'none' }}>
                            <XIcon size={15} />
                          </a>
                          <a
                            href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}&quote=${shareText}`}
                            target="_blank" rel="noopener noreferrer"
                            title="Share on Facebook"
                            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 999, background: '#1877F2', color: '#fff', textDecoration: 'none' }}>
                            <Share2 size={15} />
                          </a>
                          <a
                            href={`https://www.instagram.com/`}
                            target="_blank" rel="noopener noreferrer"
                            title="Share on Instagram (copy text)"
                            onClick={(ev) => { ev.preventDefault(); navigator.clipboard?.writeText(`I'm going to ${guide.eventName}! Get the details on VentyVIP.com!`); alert('Caption copied! Open Instagram and paste it.'); }}
                            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 999, background: 'linear-gradient(135deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)', color: '#fff', textDecoration: 'none' }}>
                            <Share2 size={15} />
                          </a>
                          <a
                            href={`https://wa.me/?text=${shareText}%20${shareUrl}`}
                            target="_blank" rel="noopener noreferrer"
                            title="Share on WhatsApp"
                            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 999, background: '#25D366', color: '#fff', textDecoration: 'none' }}>
                            <MessageCircle size={15} />
                          </a>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </Reveal>

              {!isMobile && (
                <Reveal delay={350}>
                  <div style={{
                    marginTop: 28,
                    padding: '16px 20px',
                    borderRadius: 'var(--radius)',
                    background: '#f8f9fa',
                    border: '1px dashed var(--line-2)',
                    textAlign: 'center',
                  }}>
                    <div id="hpf-ad-container" ref={(el) => {
                      if (el && !el.querySelector('script')) {
                        // Create atOptions script
                        const optionsScript = document.createElement('script');
                        optionsScript.text = `
                          atOptions = {
                            'key' : '9d913c4f563ffe7583adc6ffddd38817',
                            'format' : 'iframe',
                            'height' : 250,
                            'width' : 300,
                            'params' : {}
                          };
                        `;
                        el.appendChild(optionsScript);
                        
                        // Create invoke script
                        const invokeScript = document.createElement('script');
                        invokeScript.src = 'https://www.highperformanceformat.com/9d913c4f563ffe7583adc6ffddd38817/invoke.js';
                        invokeScript.async = true;
                        el.appendChild(invokeScript);
                      }
                    }} />
                  </div>
                </Reveal>
              )}

              <Reveal delay={400}>
                <div ref={downloadBannerRef} style={{
                  marginTop: 36,
                  padding: '20px 24px',
                  borderRadius: 'var(--radius)',
                  background: 'var(--primary-tint)',
                  border: '1px solid var(--primary-soft)',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16,
                }}>
                  <div>
                    <div style={{ fontFamily: 'var(--font-serif)', fontSize: 20, color: 'var(--ink)', marginBottom: 4 }}>
                      Want to take these details with you?
                    </div>
                    <div style={{ fontSize: 14, color: 'var(--ink-2)' }}>
                      Click Download PDF to get your event guide to-go!
                    </div>
                  </div>
                  <button onClick={downloadPDF} className="btn btn-primary">Download PDF ↓</button>
                </div>
              </Reveal>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorks = () => {
  const isMobile = useIsMobile();
  const steps = [
    {
      n: '01',
      title: 'Paste any event URL',
      body: 'Drop in the link — Eventbrite, Luma, Dice, a venue site, anywhere. If the event has a page, we can read it.',
      tags: ['Eventbrite', 'Luma', 'Dice', '+ more'],
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>,
    },
    {
      n: '02',
      title: 'Get your event guide',
      body: 'Our AI reads the event page and creates a personalized guide covering parking, public transit, dress code, arrival time, what to bring, and solo attendee tips.',
      tags: ['AI-powered', 'Instant', 'Comprehensive'],
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14,2 14,8 20,8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10,9 9,9 8,9"/></svg>,
    },
    {
      n: '03',
      title: 'Show up confident',
      body: 'No surprises. Know exactly what to expect, what to wear, when to arrive, and how to make the most of your experience.',
      tags: ['Stress-free', 'Prepared', 'Ready'],
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22,4 12,14.01 9,11.01"/></svg>,
    },
  ];

  return (
    <section id="how" style={{ padding: '100px 0 60px' }}>
      <div className="container">
        <Rule label="How it works" />
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: isMobile ? '20px' : '24px 28px',
          marginBottom: 48,
        }}>
          {steps.map((s) => (
            <div key={s.n} style={{
              background: 'var(--bg)',
              border: '1px solid var(--line)',
              borderRadius: 'var(--radius-lg)',
              padding: '28px 28px 32px',
              boxShadow: 'var(--shadow-sm)',
              transition: 'transform .2s ease, box-shadow .2s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = 'var(--shadow)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                <div className="mono" style={{ fontSize: 11, letterSpacing: '.12em', color: 'var(--ink-4)', background: 'var(--bg-2)', padding: '6px 10px', borderRadius: 999 }}>
                  Step {s.n}
                </div>
                <div style={{ color: 'var(--primary)' }}>{s.icon}</div>
              </div>
              <h3 style={{ fontSize: 24, marginBottom: 12, letterSpacing: '-0.01em' }}>{s.title}</h3>
              <p style={{ fontSize: 15, color: 'var(--ink-2)', lineHeight: 1.6, marginBottom: 18 }}>{s.body}</p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {s.tags.map(t => (
                  <span key={t} style={{ fontSize: 12, color: 'var(--ink-3)', background: 'var(--bg-2)', padding: '4px 10px', borderRadius: 999, fontFamily: 'var(--font-mono)' }}>{t}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Featured Events Section
const Featured = () => {
  const isMobile = useIsMobile();
  const [EVENTS, setEVENTS] = useState([]);
  useEffect(() => {
    fetch('/spotlight.json')
      .then(r => r.json())
      .then(data => setEVENTS(data))
      .catch(() => {});
  }, []);

  const EventCard = ({ e, featured = false }) => {
    const [imgError, setImgError] = useState(false);
    const showImage = e.image && !imgError;
    return (
    <a
      href={e.url || '#'}
      target="_blank"
      rel="noopener noreferrer"
      style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
    >
    <article className="card" style={{ display: 'flex', flexDirection: 'column', height: '100%', cursor: 'pointer' }}>
      <div style={{ position: 'relative' }}>
        {showImage ? (
          <img
            src={e.image}
            alt={e.title}
            onError={() => setImgError(true)}
            style={{
              width: '100%',
              aspectRatio: '4 / 3',
              objectFit: 'cover',
              borderRadius: 'var(--radius)',
              border: '1px solid var(--line-2)',
              display: 'block',
            }}
          />
        ) : (
          <EventVisual tone={e.tone} label={e.cat} />
        )}
        <span style={{
          position: 'absolute', top: 10, left: 10,
          padding: '5px 10px',
          borderRadius: 999,
          background: 'oklch(1 0 0 / 0.9)',
          backdropFilter: 'blur(6px)',
          fontSize: 11,
          fontFamily: 'var(--font-mono)',
          letterSpacing: '.08em',
          textTransform: 'uppercase',
          color: 'var(--ink-2)',
          border: '1px solid var(--line-2)',
        }}>{e.cat}</span>
        {e.tag && (
          <span style={{
            position: 'absolute', top: 10, right: 10,
            padding: '5px 10px', borderRadius: 999,
            background: 'var(--ink)', color: 'var(--bg)',
            fontSize: 11, fontWeight: 500,
            whiteSpace: 'nowrap',
            maxWidth: 'calc(100% - 20px)',
          }}>{e.tag}</span>
        )}
        {e.guideUrl && (
          <span style={{
            position: 'absolute', bottom: 10, left: 10,
            padding: '5px 10px', borderRadius: 999,
            background: 'var(--primary)', color: '#fff',
            fontSize: 10, fontWeight: 600,
            fontFamily: 'var(--font-mono)',
            letterSpacing: '.1em',
            textTransform: 'uppercase',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          }}>★ Featured</span>
        )}
      </div>
      <div style={{ padding: '4px 6px 10px 6px' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', color: 'var(--ink-3)', fontSize: 13, marginBottom: 8 }}>
          <span style={{ fontFamily: 'var(--font-mono)' }}>{e.date}</span>
          <span style={{ width: 3, height: 3, borderRadius: 999, background: 'var(--ink-4)' }} />
          <span>{e.time}</span>
        </div>
        <h3 style={{ fontSize: featured ? 32 : 24, letterSpacing: '-0.015em', marginBottom: 10 }}>
          {e.title}
        </h3>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, color: 'var(--ink-2)' }}>
          <span>{e.where}</span>
          <span style={{ color: 'var(--ink)', fontWeight: 500 }}>{e.price}</span>
        </div>
        {e.promoCode && (
          <div style={{
            marginTop: 10,
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '6px 10px',
            borderRadius: 6,
            background: 'oklch(0.97 0.04 145)',
            border: '1px dashed oklch(0.65 0.15 145)',
          }}>
            <span style={{ fontSize: 11, color: 'oklch(0.45 0.15 145)', fontFamily: 'var(--font-mono)', letterSpacing: '.06em', textTransform: 'uppercase' }}>Promo Code</span>
            <span style={{ fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-mono)', color: 'oklch(0.35 0.15 145)', letterSpacing: '.08em' }}>{e.promoCode}</span>
          </div>
        )}
        {e.guideUrl && (
          <span 
            role="button"
            tabIndex={0}
            onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); window.open(e.guideUrl, '_blank', 'noopener,noreferrer'); }}
            style={{
              marginTop: 12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              padding: '10px 14px',
              borderRadius: 8,
              background: 'var(--primary)',
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            📄 Get Free Guide
          </span>
        )}
      </div>
    </article>
    </a>
    );
  };

  return (
    <section id="discover" style={{ padding: '80px 0 40px' }}>
      <div className="container">
        <Rule label="EVENT SPOTLIGHT" />

        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'flex-end', flexWrap: 'wrap', gap: 24,
          marginBottom: 36,
        }}>
          <h2 style={{ fontSize: 'clamp(36px, 4.2vw, 56px)', maxWidth: 680 }}>
            What's actually <em style={{ color: 'var(--primary-strong)' }}>good</em> right now,<br/>
            sorted by real interest.
          </h2>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: 20,
        }}>
          {EVENTS.map((e, i) => (
            <Reveal key={i} delay={i * 70}><EventCard e={e} /></Reveal>
          ))}
        </div>

        <div style={{ textAlign: 'center', marginTop: 36 }}>
          <Link to="/organizers" className="btn btn-primary" style={{ background: '#f59e0b', color: '#1a1614', borderColor: '#f59e0b' }}>Get your event featured!</Link>
        </div>
      </div>
    </section>
  );
};

// Organizers Section
const Organizers = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', event: '', message: '' });
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    fetch('/api/submit-form', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ formName: 'organizer-contact', ...form }),
    }).then(() => setSent(true)).catch(() => setSent(true));
  };

  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: 8,
    border: '1px solid var(--line)', background: 'var(--bg)',
    fontSize: 14, fontFamily: 'inherit', color: 'var(--ink)',
    outline: 'none', boxSizing: 'border-box',
  };

  return (
    <section id="organizers" style={{ padding: '100px 0', position: 'relative', overflow: 'hidden' }}>
      <div style={{
        position: 'absolute', inset: 0,
        background: 'oklch(0.97 0.015 210 / 0.35)',
        backdropFilter: 'blur(20px) saturate(120%)',
        zIndex: 0,
      }} />
      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1.1fr 1fr',
          gap: 80,
          alignItems: 'center',
        }}>
          <div>
            <Rule label="For organizers" />
            <h2 style={{ fontSize: 'clamp(36px, 4.6vw, 60px)', marginBottom: 22 }}>
              List your event where <em style={{ color: 'var(--primary-strong)' }}>the right people</em> will actually see it.
            </h2>
            <p style={{ fontSize: 17, color: 'var(--ink-2)', maxWidth: 520, marginBottom: 32 }}>
              Get your event featured in our Spotlight section seen by thousands of VIPs weekly.
            </p>

            <ul style={{
              listStyle: 'none', padding: 0, margin: '0 0 36px 0',
              display: 'grid', gap: 14,
            }}>
              {[
                'Free listings, forever — no tiered visibility.',
                'Verified badge for returning organizers.',
                'Replace customer service calls! — Customers can now answer their own questions in seconds!',
              ].map(item => (
                <li key={item} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', fontSize: 15 }}>
                  <span style={{
                    flex: '0 0 18px', height: 18, borderRadius: 999,
                    background: 'var(--primary-soft)',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    marginTop: 2,
                  }}>
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="var(--primary-strong)" strokeWidth="1.8">
                      <path d="M2 5.5 4 7.5 8.5 3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                  <span style={{ color: 'var(--ink-2)' }}>{item}</span>
                </li>
              ))}
            </ul>

            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-primary" onClick={() => { setModalOpen(true); setSent(false); setForm({ name: '', email: '', event: '', message: '' }); }}>
                Contact Us to get Featured!
              </button>
            </div>

            {modalOpen && (
              <div
                onClick={(e) => { if (e.target === e.currentTarget) setModalOpen(false); }}
                style={{
                  position: 'fixed', inset: 0, zIndex: 1000,
                  background: 'oklch(0 0 0 / 0.5)',
                  backdropFilter: 'blur(4px)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: 24,
                }}>
                <div style={{
                  background: 'var(--bg)', borderRadius: 'var(--radius-lg)',
                  padding: 32, maxWidth: 480, width: '100%',
                  border: '1px solid var(--line)', position: 'relative',
                  boxShadow: 'var(--shadow)',
                }}>
                  <button
                    onClick={() => setModalOpen(false)}
                    style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-3)', display: 'inline-flex' }}>
                    <XIcon size={18} />
                  </button>
                  {sent ? (
                    <div style={{ textAlign: 'center', padding: '24px 0' }}>
                      <div style={{ fontSize: 36, marginBottom: 12 }}>🎉</div>
                      <h3 style={{ marginBottom: 8 }}>Got it! We'll be in touch.</h3>
                      <p style={{ color: 'var(--ink-2)', fontSize: 14 }}>We typically respond within 1–2 business days.</p>
                      <button className="btn btn-ghost" style={{ marginTop: 20 }} onClick={() => setModalOpen(false)}>Close</button>
                    </div>
                  ) : (
                    <>
                      <h3 style={{ fontSize: 22, marginBottom: 6 }}>Get your event featured</h3>
                      <p style={{ fontSize: 14, color: 'var(--ink-2)', marginBottom: 24 }}>Tell us about your event and we'll reach out with pricing and details.</p>
                      <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 14 }}>
                        <input type="text" name="name" required placeholder="Your name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} />
                        <input type="email" name="email" required placeholder="Email address" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} style={inputStyle} />
                        <input type="text" name="event" required placeholder="Event name" value={form.event} onChange={e => setForm(f => ({ ...f, event: e.target.value }))} style={inputStyle} />
                        <textarea name="message" placeholder="Tell us more (date, location, goals...)" value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} rows={4} style={{ ...inputStyle, resize: 'vertical' }} />
                        <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}>Send Request</button>
                      </form>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          <div style={{
            background: 'var(--bg)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-lg)',
            padding: 22,
          }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--ink-4)', marginBottom: 12 }}>How your event appears on VentyVIP</div>
            <div style={{ background: 'var(--bg-2)', borderRadius: 12, padding: 18, border: '1px solid var(--line)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{ background: 'var(--primary)', color: '#fff', fontSize: 9, fontWeight: 700, padding: '3px 8px', borderRadius: 999, fontFamily: 'var(--font-mono)', letterSpacing: '.08em' }}>FEATURED</span>
                <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>Spotlight Event</span>
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--ink)', marginBottom: 6 }}>Your Event Name Here</div>
              <div style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 12, lineHeight: 1.5 }}>A compelling one-liner about your event that grabs attention.</div>
              <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--ink-3)' }}>
                <span>📅 Sat, Jun 14</span>
                <span>📍 Your Venue</span>
                <span>🎟️ From $25</span>
              </div>
              <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--line)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>Seen by 248,000+ VentyVIP users</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--primary-strong)' }}>📄 Get Guide →</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Newsletter Section
const Newsletter = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  return (
    <section id="newsletter" style={{ padding: '100px 0 80px', position: 'relative', overflow: 'hidden' }}>
      <Aurora intensity={window.__TWEAKS__.auroraIntensity} grain={false}/>
      <div className="container" style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
        <h2 style={{ fontSize: 'clamp(40px, 5.5vw, 72px)', maxWidth: 820, margin: '0 auto 22px' }}>
          The Thursday <em style={{ color: 'var(--primary-strong)' }}>drop</em>.
        </h2>
        <p style={{ fontSize: 17, color: 'var(--ink-2)', maxWidth: 540, margin: '0 auto 36px' }}>
          Handpicked events, discount codes, useful tips, and more! Unsubscribe in one click.
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            fetch('/api/submit-form', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ formName: 'newsletter', email }),
            }).then(() => setSubmitted(true)).catch(() => setSubmitted(true));
          }}
          style={{
            maxWidth: 480, margin: '0 auto',
            display: 'flex', gap: 6,
            background: 'var(--bg)',
            border: '1px solid var(--line)',
            borderRadius: 999,
            padding: 6,
            boxShadow: 'var(--shadow)',
          }}>
          <input
            type="email"
            name="email"
            required
            placeholder="you@somewhere.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{
              flex: 1,
              border: 0, outline: 'none',
              background: 'transparent',
              fontSize: 15, padding: '12px 18px',
              fontFamily: 'inherit',
              color: 'var(--ink)',
            }}
          />
          <button type="submit" className="btn btn-primary" style={{ padding: '12px 22px' }}>
            {submitted ? 'Subscribed! ✓' : 'Subscribe'}
          </button>
        </form>

        <div style={{ marginTop: 18, fontSize: 12, color: 'var(--ink-3)', fontFamily: 'var(--font-mono)' }}>
          Joining THOUSANDS of curious attendees  across the US.
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const em = ['info', 'VentyVIP', 'com'];
  const cols = [
    { t: 'Discover', items: [
      { label: 'This weekend', href: '#' },
      { label: 'Concerts', href: '#' },
      { label: 'Running', href: '#' },
      { label: 'Food & drink', href: '#' },
      { label: 'Tech', href: '#' },
      { label: 'Free events', href: '#' },
    ]},
    { t: 'Organizers', items: [
      { label: 'List an event', href: '/organizers' },
    ]},
    { t: 'Company', items: [
      { label: 'About', href: '/about' },
      { label: 'Contact', href: '#', onClick: (e) => { e.preventDefault(); window.location.href = 'mai' + 'lto:' + em[0] + '@' + em[1] + '.' + em[2] + '?subject=VentyVIP%20Inquiry'; } },
    ]},
    { t: 'Legal', items: [
      { label: 'Privacy', href: '/privacy' },
      { label: 'Terms', href: '/terms' },
      { label: 'Accessibility', href: '#' },
      { label: 'Cookies', href: '/cookies' },
    ]},
  ];

  return (
    <footer style={{ padding: '60px 0 40px', borderTop: '1px solid var(--line)' }}>
      <div className="container" style={{
        display: 'grid',
        gridTemplateColumns: '1.4fr repeat(4, 1fr)',
        gap: 40, marginBottom: 60,
      }}>
        <div>
          <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
            <img 
              src="/ventyvip-logo.png" 
              alt="VentyVIP Logo"
              style={{
                width: 32,
                height: 'auto',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
              }}
            />
            <Wordmark size={28} />
          </div>
          <p style={{ fontSize: 14, color: 'var(--ink-2)', maxWidth: 280, lineHeight: 1.6 }}>
            AI-powered event discovery for first-timers and solo attendees.
            Get the intel you need to show up confident.
          </p>
        </div>
        {cols.map(c => (
          <div key={c.t}>
            <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', letterSpacing: '.14em', textTransform: 'uppercase', marginBottom: 16 }}>{c.t}</div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 10, fontSize: 14 }}>
              {c.items.map(item => (
                <li key={item.label}>
                  {item.onClick || item.href === '#' ? (
                    <a href={item.href} onClick={item.onClick} style={{ color: 'var(--ink-2)' }}>{item.label}</a>
                  ) : (
                    <Link to={item.href} style={{ color: 'var(--ink-2)', textDecoration: 'none' }}>{item.label}</Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="container" style={{
        display: 'flex', justifyContent: 'space-between',
        fontSize: 12, color: 'var(--ink-4)',
        fontFamily: 'var(--font-mono)',
        letterSpacing: '.06em',
        borderTop: '1px solid var(--line-2)',
        paddingTop: 24,
      }}>
        <span>© CROPTILL LLC</span>
        <span>v2.4 · nyc · la · chi · atx · mia · sea</span>
      </div>
    </footer>
  );
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Redirect old hash-based URLs to clean paths for backward compat
const HashRedirect = () => {
  const navigate = useNavigate();
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.length > 1) {
      // Map old hash routes to new paths
      const hashPath = hash.replace(/^#\/?/, '/');
      // Only redirect known page routes, not section anchors like #discover, #how, #newsletter
      const knownRoutes = ['/about', '/privacy', '/terms', '/cookies', '/blog', '/organizers'];
      if (knownRoutes.some(r => hashPath.startsWith(r))) {
        window.history.replaceState(null, '', hashPath);
        navigate(hashPath, { replace: true });
      }
    }
  }, [navigate]);
  return null;
};

// Home page layout
const HomePage = ({ tweaks, setTweaks, panelOpen, setPanelOpen }) => (
  <>
    <Nav />
    <Hero />
    <HowItWorks />
    <Featured />
    <Organizers />
    <Newsletter />
    <Footer />
    <TweaksPanel tweaks={tweaks} setTweaks={setTweaks} open={panelOpen} onClose={() => setPanelOpen(false)} />
    <CookieBanner />
  </>
);

// Main App
export const App = () => {
  const [tweaks, setTweaks] = useState(window.__TWEAKS__);
  const [panelOpen, setPanelOpen] = useState(false);

  useEffect(() => {
    document.documentElement.setAttribute('data-primary', tweaks.primary);
    document.documentElement.setAttribute('data-aurora', tweaks.auroraIntensity);
    const fontValue = tweaks.titleFont === 'sans'
      ? "'Plus Jakarta Sans', ui-sans-serif, 'Helvetica Neue', sans-serif"
      : "'Instrument Serif', 'Iowan Old Style', Georgia, serif";
    document.documentElement.style.setProperty('--font-serif', fontValue);
    window.__TWEAKS__ = tweaks;
  }, [tweaks]);

  useEffect(() => {
    const handler = (ev) => {
      if (ev.data?.type === '__activate_edit_mode') setPanelOpen(true);
      if (ev.data?.type === '__deactivate_edit_mode') setPanelOpen(false);
    };
    window.addEventListener('message', handler);
    window.parent?.postMessage({ type: '__edit_mode_available' }, '*');
    return () => window.removeEventListener('message', handler);
  }, []);

  return (
    <>
      <ScrollToTop />
      <HashRedirect />
      <Routes>
        <Route path="/" element={<HomePage tweaks={tweaks} setTweaks={setTweaks} panelOpen={panelOpen} setPanelOpen={setPanelOpen} />} />
        <Route path="/about" element={<About />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/cookies" element={<Cookies />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/*" element={<Blog />} />
        <Route path="/organizers" element={<ForOrganizers />} />
        <Route path="/organizers/consultation" element={<Consultation />} />
        <Route path="/organizers/playbook" element={<Playbook />} />
        <Route path="/organizers/photography" element={<Photography />} />
        <Route path="/shop" element={<Shop />} />
      </Routes>
    </>
  );
};
